import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useEntityDoc, useEntityList, createEntity, deleteEntity } from '../data/entities';
import { useAuth } from '../AuthContext';
import WorkspacePipelines from '../components/WorkspacePipelines';
import WorkspaceWorkloads from '../components/WorkspaceWorkloads';
import WorkspaceDatasets from '../components/WorkspaceDatasets';
import WorkspaceDevSpaces from '../components/WorkspaceDevSpaces';

// Workspace home: projects live here; pipelines and compute workloads get
// their own sections (added by their phases' components).
export default function WorkspaceDetail() {
  const { orgId, workspaceId } = useParams();
  const { doc: ws } = useEntityDoc('workspace', { org: orgId }, workspaceId);

  if (ws === undefined) return <div className="cs-page"><p className="cs-help">Loading…</p></div>;
  if (ws === null) {
    return (
      <div className="cs-page">
        <p className="cs-help">Workspace not found. <Link to={`/orgs/${orgId}`}>Back</Link></p>
      </div>
    );
  }

  return (
    <div className="cs-page" data-nova-note="A workspace: its projects, datasets, workloads and pipelines; members seated here.">
      <p style={{ margin: 0 }}>
        <Link to={`/orgs/${orgId}`} className="cs-help" style={{ textDecoration: 'none' }}>← Organization</Link>
      </p>
      <h1 style={{ margin: '8px 0 0' }}>{ws.name}</h1>
      {ws.description && <p className="cs-help" style={{ marginTop: 6 }}>{ws.description}</p>}
      <p className="cs-help" style={{ marginTop: 6, fontSize: 12 }}>
        Also in this organization: <Link to={`/orgs/${orgId}/agents`}>Agents</Link> ·{' '}
        <Link to={`/orgs/${orgId}/review`}>Review queue</Link> · <Link to={`/orgs/${orgId}/apps`}>Apps</Link>
      </p>

      <ProjectsSection orgId={orgId} workspaceId={workspaceId} />
      <WorkspacePipelines orgId={orgId} workspaceId={workspaceId} />
      <WorkspaceWorkloads orgId={orgId} workspaceId={workspaceId} />
      <WorkspaceDatasets orgId={orgId} workspaceId={workspaceId} />
      <WorkspaceDevSpaces orgId={orgId} workspaceId={workspaceId} />
    </div>
  );
}

function ProjectsSection({ orgId, workspaceId }) {
  const { user } = useAuth();
  const scope = { org: orgId, workspace: workspaceId };
  const { docs: projects } = useEntityList('project', scope);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  return (
    <section style={{ marginTop: 24 }}>
      <h2 style={{ margin: 0 }}>Projects</h2>
      <form
        style={{ display: 'flex', gap: 8, marginTop: 12, maxWidth: 480 }}
        onSubmit={async (e) => {
          e.preventDefault();
          if (!name.trim() || busy) return;
          setBusy(true);
          try {
            await createEntity('project', scope, { name: name.trim(), createdBy: user.uid });
            setName('');
          } finally {
            setBusy(false);
          }
        }}
      >
        <input
          className="cs-input"
          placeholder="New project name…"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <button className="cs-btn cs-btn-primary" disabled={!name.trim() || busy} type="submit">
          Create
        </button>
      </form>

      {projects && !projects.length && (
        <div className="cs-empty" style={{ marginTop: 14 }}>No projects yet.</div>
      )}
      <div style={{ display: 'grid', gap: 10, marginTop: 14 }}>
        {(projects || []).map((p) => (
          <Link
            key={p.id}
            to={`/orgs/${orgId}/p/${workspaceId}/${p.id}`}
            className="cs-card"
            style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}
          >
            <strong style={{ color: 'var(--text)' }}>{p.name}</strong>
            <span style={{ flex: 1 }} />
            <button
              className="cs-btn cs-btn-outline cs-btn-sm"
              onClick={async (e) => {
                e.preventDefault();
                // eslint-disable-next-line no-alert
                if (window.confirm(`Delete project "${p.name}"?`)) {
                  await deleteEntity('project', scope, p.id);
                }
              }}
            >
              Delete
            </button>
          </Link>
        ))}
      </div>
    </section>
  );
}
